import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, ShieldCheck, Truck, RotateCcw } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { useCurrency } from "../context/CurrencyContext";
import { createOrder, checkPromo, fetchCarriers, createStripeCheckout, resolveMediaUrl } from "../lib/api";
import Navbar from "../components/Navbar";
import { toast } from "sonner";

const Checkout = () => {
  const { items, subtotal, clear } = useCart();
  const { user } = useAuth();
  const { formatPrice, currency } = useCurrency();
  const navigate = useNavigate();
  const [promos, setPromos] = useState([]); // stack: [{code, discount, label, kind}]
  const [promoInput, setPromoInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    email: "", full_name: "", phone: "", address: "", city: "", postal_code: "", country: "France",
  });
  const [placed, setPlaced] = useState(null);
  const [carriers, setCarriers] = useState([]);
  const [carrierSlug, setCarrierSlug] = useState("");

  // Prefill from connected customer
  useEffect(() => {
    if (user && user.email) {
      setForm((prev) => ({
        ...prev,
        email: prev.email || user.email,
        full_name: prev.full_name || user.full_name || "",
      }));
    }
  }, [user]);

  useEffect(() => {
    fetchCarriers()
      .then((list) => {
        setCarriers(list);
        if (list.length > 0) setCarrierSlug(list[0].slug);
      })
      .catch(console.error);
  }, []);

  const selectedCarrier = carriers.find((c) => c.slug === carrierSlug);
  const isFrance = (form.country || "").trim().toLowerCase().replace(/\s+/g, "") === "france"
    || (form.country || "").trim().toUpperCase() === "FR";
  const freeShippingThreshold = isFrance ? 60 : 160;
  const freeShippingApplies = subtotal >= freeShippingThreshold;
  const shipping = freeShippingApplies ? 0 : (selectedCarrier ? selectedCarrier.price : 0);
  const totalDiscount = promos.reduce((s, p) => s + (p.discount || 0), 0);
  const total = Math.max(0, subtotal - totalDiscount + shipping);

  const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const applyPromo = async () => {
    if (!promoInput.trim()) return;
    try {
      const res = await checkPromo(promoInput, subtotal, promos.map((p) => p.code));
      setPromos((prev) => [...prev, { code: res.code, discount: res.discount, label: res.label, kind: res.kind }]);
      setPromoInput("");
      toast.success(`Code appliqué : ${res.label}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Code promo invalide");
    }
  };

  const removePromo = (code) => setPromos((prev) => prev.filter((p) => p.code !== code));

  const submit = async (e) => {
    e.preventDefault();
    if (items.length === 0) {
      toast.error("Votre écrin est vide");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        items: items.map((i) => ({
          product_id: i.product_id, name: i.name, volume: i.volume, quantity: i.quantity, price: i.price,
        })),
        subtotal, shipping, total,
        promo_code: promos.length > 0 ? promos.map((p) => p.code).join("+") : null,
        carrier_slug: carrierSlug || null,
        carrier_name: selectedCarrier ? selectedCarrier.name : null,
      };
      // 1. Create the order (status "nouveau", will move to "en_attente_paiement" after Stripe session)
      const order = await createOrder(payload);

      // 2. Create Stripe Checkout Session and redirect
      const stripePayload = {
        order_id: order.id,
        origin_url: window.location.origin,
        items: items.map((i) => ({
          name: i.name,
          price: i.price,
          quantity: i.quantity,
          volume: i.volume,
          image: i.image && i.image.startsWith("http") ? i.image : null,
        })),
        shipping_amount: shipping,
        shipping_label: selectedCarrier ? `Livraison — ${selectedCarrier.name}` : "Livraison",
        discount_amount: totalDiscount || 0,
        discount_label: promos.length > 0 ? promos.map((p) => p.label).join(" + ") : null,
        customer_email: form.email,
      };
      const { checkout_url } = await createStripeCheckout(stripePayload);
      if (!checkout_url) throw new Error("Session Stripe indisponible");
      // Full navigation to Stripe hosted page
      window.location.href = checkout_url;
    } catch (err) {
      console.error(err);
      toast.error("Impossible d'ouvrir le paiement. Veuillez réessayer.");
      setLoading(false);
    }
  };

  if (placed) {
    return (
      <div className="bg-[#050505] min-h-screen">
        <Navbar />
        <div className="pt-40 pb-24 max-w-3xl mx-auto px-6 md:px-12 text-center">
          <div className="w-16 h-16 mx-auto rounded-full border border-[color:var(--gold)] flex items-center justify-center mb-8">
            <Check size={24} className="text-[color:var(--gold)]" />
          </div>
          <p className="label-caps text-[color:var(--gold)] mb-4">Commande confirmée</p>
          <h1 data-testid="order-confirmed" className="font-serif text-5xl md:text-6xl text-white mb-6 leading-tight">
            Merci, <em className="italic text-[color:var(--gold-soft)]">{form.full_name.split(" ")[0]}</em>.
          </h1>
          <p className="text-white/60 font-light max-w-xl mx-auto mb-12">
            Votre commande <span className="text-white">#{placed.id.slice(0, 8).toUpperCase()}</span> a bien été reçue. Un email de confirmation vient d'être envoyé à <span className="text-white">{form.email}</span>.
          </p>
          <button onClick={() => navigate("/")} data-testid="back-home-btn" className="btn-outline">Retour à l'accueil</button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="checkout-page" className="bg-[#050505] min-h-screen">
      <Navbar />

      <div className="pt-32 pb-24 max-w-[1400px] mx-auto px-6 md:px-12">
        <button onClick={() => navigate(-1)} className="label-caps text-white/50 hover:text-[color:var(--gold)] inline-flex items-center gap-3 mb-10" data-testid="checkout-back">
          <ArrowLeft size={14} /> Retour
        </button>

        <div className="mb-12">
          <p className="label-caps text-[color:var(--gold)] mb-4">Finalisation</p>
          <h1 className="font-serif text-4xl md:text-5xl text-white">Votre <em className="italic text-[color:var(--gold-soft)]">commande</em></h1>
        </div>

        {/* Progress */}
        <div className="flex items-center gap-4 mb-16 label-caps text-[10px] text-white/40">
          <span className="text-[color:var(--gold)]">01 Écrin</span>
          <span className="flex-1 h-px bg-[color:var(--gold)]/30" />
          <span className="text-[color:var(--gold)]">02 Coordonnées</span>
          <span className="flex-1 h-px bg-white/10" />
          <span>03 Confirmation</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
          {/* Form */}
          <form onSubmit={submit} className="lg:col-span-2 space-y-10">
            <div>
              <p className="label-caps text-[color:var(--gold)] mb-8">Vos coordonnées</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-6">
                <input data-testid="checkout-email" required type="email" placeholder="Email" className="input-line md:col-span-2" value={form.email} onChange={change("email")} />
                <input data-testid="checkout-name" required placeholder="Nom complet" className="input-line" value={form.full_name} onChange={change("full_name")} />
                <input data-testid="checkout-phone" required placeholder="Téléphone" className="input-line" value={form.phone} onChange={change("phone")} />
                <input data-testid="checkout-address" required placeholder="Adresse" className="input-line md:col-span-2" value={form.address} onChange={change("address")} />
                <input data-testid="checkout-city" required placeholder="Ville" className="input-line" value={form.city} onChange={change("city")} />
                <input data-testid="checkout-postal" required placeholder="Code postal" className="input-line" value={form.postal_code} onChange={change("postal_code")} />
                <input data-testid="checkout-country" required placeholder="Pays" className="input-line md:col-span-2" value={form.country} onChange={change("country")} />
              </div>
            </div>

            <div>
              <p className="label-caps text-[color:var(--gold)] mb-8">Mode de livraison</p>
              {carriers.length === 0 ? (
                <p className="text-white/50 text-sm italic">Chargement des transporteurs...</p>
              ) : (
                <div className="space-y-3">
                  {carriers.map((c) => (
                    <label
                      key={c.id}
                      data-testid={`carrier-option-${c.slug}`}
                      className={`flex items-start gap-4 p-5 border cursor-pointer transition-all ${
                        carrierSlug === c.slug
                          ? "border-[color:var(--gold)] bg-[color:var(--gold)]/5"
                          : "border-white/10 hover:border-white/25"
                      }`}
                    >
                      <input
                        type="radio"
                        name="carrier"
                        value={c.slug}
                        checked={carrierSlug === c.slug}
                        onChange={() => setCarrierSlug(c.slug)}
                        className="mt-1 accent-[color:var(--gold)]"
                      />
                      <div className="flex-1">
                        <div className="flex items-baseline justify-between gap-3">
                          <p className="font-serif text-lg text-white">{c.name}</p>
                          <p className="font-serif text-[color:var(--gold)] text-lg">
                            {freeShippingApplies ? (
                              <><span className="text-white/40 line-through mr-2 text-sm">{c.price.toFixed(2)}€</span> Offert</>
                            ) : (
                              <>{c.price.toFixed(2)}€</>
                            )}
                          </p>
                        </div>
                        <p className="text-white/50 font-light text-sm mt-1">{c.description}</p>
                        <p className="label-caps text-white/40 text-[10px] mt-2">Délai : {c.delivery_time}</p>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div>
              <p className="label-caps text-[color:var(--gold)] mb-8">Paiement</p>
              <div className="border border-white/10 p-8">
                <p className="text-white/60 font-light text-sm mb-4">
                  Vous serez redirigé vers Stripe pour finaliser votre paiement en toute sécurité.
                  Chiffrement SSL 256-bit, aucune donnée bancaire stockée sur nos serveurs.
                </p>
                <div className="flex items-center gap-4 label-caps text-white/40 text-[10px]">
                  <span>Visa</span><span>·</span><span>Mastercard</span><span>·</span><span>Amex</span><span>·</span><span>Apple Pay</span><span>·</span><span>Google Pay</span>
                </div>
              </div>
            </div>

            <button data-testid="checkout-submit" type="submit" disabled={loading || items.length === 0} className="btn-gold w-full">
              {loading ? "Redirection vers Stripe..." : <>Payer {formatPrice(total)}{currency !== "EUR" && ` (≈ ${total.toFixed(2)}€ facturés)`} · Paiement sécurisé <ArrowRight size={14} /></>}
            </button>

            <div className="grid grid-cols-3 gap-4 pt-6">
              <div className="flex items-center gap-3"><ShieldCheck size={16} className="text-[color:var(--gold)]" strokeWidth={1.2} /><span className="label-caps text-white/50 text-[9px]">Sécurisé</span></div>
              <div className="flex items-center gap-3"><Truck size={16} className="text-[color:var(--gold)]" strokeWidth={1.2} /><span className="label-caps text-white/50 text-[9px]">Livré 48–72h</span></div>
              <div className="flex items-center gap-3"><RotateCcw size={16} className="text-[color:var(--gold)]" strokeWidth={1.2} /><span className="label-caps text-white/50 text-[9px]">Retours 14j</span></div>
            </div>
          </form>

          {/* Summary */}
          <aside data-testid="checkout-summary" className="lg:sticky lg:top-28 self-start border border-white/10 p-8">
            <p className="label-caps text-[color:var(--gold)] mb-6">Récapitulatif</p>
            <ul className="divide-y divide-white/5 mb-6">
              {items.map((it) => (
                <li key={it.key} className="flex gap-4 py-4">
                  <img src={resolveMediaUrl(it.image)} alt={it.name} className="w-16 h-20 object-cover" />
                  <div className="flex-1">
                    <p className="font-serif text-white">{it.name}</p>
                    <p className="label-caps text-white/40 text-[9px] mt-1">{it.volume} · Qté {it.quantity}</p>
                  </div>
                  <p className="text-[color:var(--gold)] font-serif">{formatPrice(it.price * it.quantity, { decimals: 0 })}</p>
                </li>
              ))}
            </ul>

            <div className="flex items-center gap-3 mb-4">
              <input data-testid="promo-input" value={promoInput} onChange={(e) => setPromoInput(e.target.value)} placeholder="Code promo" className="input-line flex-1" />
              <button type="button" data-testid="promo-apply" onClick={applyPromo} className="label-caps text-[color:var(--gold)] text-[10px] border-b border-[color:var(--gold)]">Appliquer</button>
            </div>
            {promos.length > 0 && (
              <ul className="mb-4 space-y-1" data-testid="applied-codes">
                {promos.map((p) => (
                  <li key={p.code} className="flex items-center justify-between text-xs text-[color:var(--gold)]/80 bg-[color:var(--gold)]/[0.05] px-3 py-2 border border-[color:var(--gold)]/20">
                    <span className="label-caps tracking-[0.15em] text-[10px]">
                      ✦ {p.code}
                    </span>
                    <button
                      type="button"
                      onClick={() => removePromo(p.code)}
                      className="text-white/40 hover:text-red-400 text-[10px] label-caps"
                      data-testid={`remove-promo-${p.code}`}
                    >
                      Retirer
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <p className="text-white/40 text-xs italic mb-6">Cumulez « BIENVENUE » (−15%) et une carte cadeau ou vos points fidélité.</p>

            <div className="space-y-3 border-t border-white/10 pt-6 text-sm">
              <div className="flex justify-between text-white/60"><span>Sous-total</span><span data-testid="summary-subtotal">{formatPrice(subtotal)}</span></div>
              {promos.map((p) => (
                <div key={p.code} className="flex justify-between text-[color:var(--gold)]"><span>{p.label}</span><span>−{formatPrice(p.discount)}</span></div>
              ))}
              <div className="flex justify-between text-white/60">
                <span>Livraison {selectedCarrier && <span className="text-white/40 text-xs">· {selectedCarrier.name}</span>}</span>
                <span>{shipping === 0 ? "Offerte" : formatPrice(shipping)}</span>
              </div>
              <div className="flex justify-between text-white font-serif text-xl pt-4 border-t border-white/10">
                <span>Total</span><span data-testid="summary-total">{formatPrice(total)}</span>
              </div>
              {currency !== "EUR" && (
                <p className="text-[10px] text-white/40 italic text-right pt-2">Devise de facturation : EUR · Total facturé : {total.toFixed(2)}€</p>
              )}
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
